import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

interface EmployeesSettings {
  allow_customer_selection: boolean;
  allow_no_preference: boolean;
  employee_step_before_service: boolean;
}

const defaultSettings: EmployeesSettings = {
  allow_customer_selection: true,
  allow_no_preference: true,
  employee_step_before_service: false,
};

const EmployeesExtensionSettings = () => {
  const { t } = useLanguage();
  const { profile } = useAuth();
  const { toast } = useToast();
  const [settings, setSettings] = useState<EmployeesSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const orgId = profile?.organisation_id;

  useEffect(() => {
    if (!orgId) return;
    supabase
      .from("organisation_extensions")
      .select("settings")
      .eq("organisation_id", orgId)
      .eq("extension_id", "employees")
      .maybeSingle()
      .then(({ data }) => {
        if (data?.settings && typeof data.settings === "object") {
          setSettings({ ...defaultSettings, ...(data.settings as any) });
        }
        setLoading(false);
      });
  }, [orgId]);

  const handleSave = async () => {
    if (!orgId) return;
    setSaving(true);
    const { error } = await supabase
      .from("organisation_extensions")
      .update({ settings: settings as any })
      .eq("organisation_id", orgId)
      .eq("extension_id", "employees");
    setSaving(false);
    if (error) {
      toast({ title: t("ext.save_error"), variant: "destructive" });
    } else {
      toast({ title: t("ext.saved") });
    }
  };

  if (loading) {
    return <div className="p-6 text-muted-foreground text-sm">{t("ext.loading")}</div>;
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-xl font-bold text-foreground mb-1">{t("employees.ext_title")}</h1>
      <p className="text-sm text-muted-foreground mb-6">{t("employees.ext_subtitle")}</p>

      <section className="rounded-xl border border-border bg-card p-6 shadow-card space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium">{t("employees.allow_customer_selection")}</Label>
            <p className="text-xs text-muted-foreground mt-0.5">{t("employees.allow_customer_selection_desc")}</p>
          </div>
          <Switch
            checked={settings.allow_customer_selection}
            onCheckedChange={(v) => setSettings((prev) => ({ ...prev, allow_customer_selection: v }))}
          />
        </div>

        {settings.allow_customer_selection && (
          <>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">{t("employees.allow_no_preference")}</Label>
                <p className="text-xs text-muted-foreground mt-0.5">{t("employees.allow_no_preference_desc")}</p>
              </div>
              <Switch
                checked={settings.allow_no_preference}
                onCheckedChange={(v) => setSettings((prev) => ({ ...prev, allow_no_preference: v }))}
              />
            </div>

            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">{t("employees.employee_step_before_service")}</Label>
                <p className="text-xs text-muted-foreground mt-0.5">{t("employees.employee_step_before_service_desc")}</p>
              </div>
              <Switch
                checked={settings.employee_step_before_service}
                onCheckedChange={(v) => setSettings((prev) => ({ ...prev, employee_step_before_service: v }))}
              />
            </div>
          </>
        )}
      </section>

      <Button onClick={handleSave} disabled={saving} className="mt-6">
        {saving ? t("employees.saving") : t("employees.save_settings")}
      </Button>
    </div>
  );
};

export default EmployeesExtensionSettings;
